export class LineIndex {
  text: string;
  lines: { offset: number; text: string; id?: string }[];

  constructor(text: string, opts?: { lineIds?: string[] }) {
    this.text = text;
    this.lines = [];
    let start = 0;
    while (start < text.length) {
      const newlineIndex = text.indexOf('\n', start);
      if (newlineIndex === -1) {
        // No more newlines; push the last line (even if it's empty)
        const lineText = text.substring(start);
        const index = this.lines.length;
        const id = opts?.lineIds?.[index] ?? String(index + 1);
        this.lines.push({ offset: start, text: lineText, id });
        break;
      } else {
        let end = newlineIndex;
        // If the line ends with \r\n, remove the \r from the line text.
        if (end > start && text[end - 1] === '\r') {
          end = newlineIndex - 1;
        }
        const line = text.substring(start, end);
        const index = this.lines.length;
        const id = opts?.lineIds?.[index] ?? String(index + 1);
        this.lines.push({ offset: start, text: line, id });
        start = newlineIndex + 1;
      }
    }
  }

  offsetToLineInfo(offset: number): {
    text: string;
    line: number;
    column: number;
    id?: string;
  } {
    const lines = this.lines;

    // Handle empty lines array - fail fast
    if (lines.length === 0) {
      return { text: '', line: 1, column: 1, id: undefined };
    }

    let low = 0;
    let high = lines.length - 1;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (lines[mid].offset <= offset) {
        if (mid === lines.length - 1 || lines[mid + 1].offset > offset) {
          return {
            text: lines[mid].text,
            line: mid + 1,
            column: offset - lines[mid].offset + 1,
            id: lines[mid].id
          };
        }
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    // Fallback: if not found, return the text of the first line and computed start column.
    return {
      text: lines[0].text,
      line: 1,
      column: offset + 1,
      id: lines[0].id
    };
  }

  offsetToLineColumn(offset: number): {
    text: string;
    line: number;
    column: number;
  } {
    const info = this.offsetToLineInfo(offset);
    return { text: info.text, line: info.line, column: info.column };
  }

  toJSON(opts?: { compact?: boolean }): unknown {
    if (opts?.compact) {
      return this.lines.map((line, index) => `${index} @ ${line.offset}: '${line.text}'`);
    }
    return {
      text: this.text,
      lines: this.lines
    };
  }
}
